import { useCallback, useContext, useEffect } from 'react';

import { SocketContext } from '../contexts/socket-context';
import type { ClientToServerEvents, ServerToClientEvents } from '../lib/socket-client';

/**
 * Hook to access socket context
 */
export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
}

/**
 * Hook to subscribe to a specific socket event
 */
export function useSocketEvent<K extends keyof ServerToClientEvents>(
  event: K,
  handler: ServerToClientEvents[K]
) {
  const { socket, isConnected } = useSocket();

  useEffect(() => {
    if (!socket || !isConnected) return;

    socket.on(event, handler as never);

    return () => {
      socket.off(event, handler as never);
    };
  }, [socket, isConnected, event, handler]);
}

/**
 * Hook to emit events to the server
 */
export function useSocketEmit() {
  const { socket, isConnected } = useSocket();

  const emit = useCallback(
    <K extends keyof ClientToServerEvents>(
      event: K,
      ...args: Parameters<ClientToServerEvents[K]>
    ) => {
      if (!socket || !isConnected) {
        console.warn('Socket is not connected, cannot emit event:', event);
        return false;
      }
      socket.emit(event, ...args);
      return true;
    },
    [socket, isConnected]
  );

  return emit;
}

/**
 * Hook for ping/pong latency testing
 */
export function useSocketPing() {
  const { socket, isConnected } = useSocket();

  const ping = useCallback((): Promise<number> => {
    return new Promise((resolve, reject) => {
      if (!socket || !isConnected) {
        reject(new Error('Socket is not connected'));
        return;
      }

      const start = Date.now();
      socket.emit('ping', (_response) => {
        const latency = Date.now() - start;
        resolve(latency);
      });

      // Timeout after 5 seconds
      setTimeout(() => {
        reject(new Error('Ping timeout'));
      }, 5000);
    });
  }, [socket, isConnected]);

  return ping;
}
